import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Rating,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Close, Star } from '@mui/icons-material';
import api from '../../utils/api';
import { Agent } from '../../types';
import ToastNotification from '../../utils/toastNotification';

interface RateAgentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  agent: Agent | null;
}

const LABELS: Record<number, string> = {
  0.5: 'Poor',
  1: 'Poor+',
  1.5: 'Below Average',
  2: 'Below Average+',
  2.5: 'Average',
  3: 'Average+',
  3.5: 'Good',
  4: 'Good+',
  4.5: 'Excellent',
  5: 'Outstanding',
};

const RateAgentDialog: React.FC<RateAgentDialogProps> = ({ open, onClose, onSuccess, agent }) => {
  const [rating, setRating] = useState<number | null>(null);
  const [hover, setHover] = useState<number>(-1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (agent) {
      setRating(agent.rating || null);
    }
  }, [agent]);

  const handleSubmit = async () => {
    if (!agent || rating === null) {
      ToastNotification.warning('Please select a star rating');
      return;
    }

    setLoading(true);
    try {
      await api.patch(`/agents/${agent.id}/rating`, { rating });
      ToastNotification.success('Agent rated successfully!');
      onSuccess();
      handleClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to rate agent';
      ToastNotification.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setHover(-1);
    onClose();
  };

  const agentName = agent
    ? `${agent.user?.firstName ?? ''} ${agent.user?.lastName ?? ''}`.trim()
    : '';

  const activeValue = hover !== -1 ? hover : rating;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Star color="warning" />
          <Typography variant="h6" fontWeight={600}>
            Rate Agent
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={loading}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Typography variant="body1" color="text.secondary" textAlign="center" mb={1}>
          {agentName}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 2 }}>
          <Rating
            value={rating}
            precision={0.5}
            size="large"
            onChange={(_e, newValue) => setRating(newValue)}
            onChangeActive={(_e, newHover) => setHover(newHover)}
            sx={{ fontSize: '3rem' }}
          />
          <Typography variant="body2" color="text.secondary" minHeight={24}>
            {activeValue !== null && activeValue > 0 ? LABELS[activeValue] ?? '' : 'Select a rating'}
          </Typography>
          {rating !== null && (
            <Typography variant="caption" color="primary" fontWeight={600}>
              Current rating: {rating.toFixed(1)} / 5
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={loading || rating === null}
          startIcon={loading ? <CircularProgress size={18} /> : <Star />}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
        >
          {loading ? 'Saving...' : 'Save Rating'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RateAgentDialog;
